import { useTranslation } from 'react-i18next'
import { Button, MenuItem, TextField } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import Dialog from '../common/dialog'
import { customersBankAccountType } from '../../lib'
import DangerIcon from '../../assets/icons/danger-icon.svg'
import WarningIcon from '../../assets/icons/warning-icon.svg'
import { RegisteredBankAccountItem } from './registered-bank-account-item'
import { Bank, BankAccount, Customer, FormError, Payment } from '../../types'
import { baseButtonTextWhiteBg, textFieldWhiteBg } from '../../theme/custom-styles'

export const BankAccountNoveltyForm = forwardRef(
  (
    {
      banksData,
      errors,
      paymentData,
      paymentCustomerData,
      handleResolveSelectedNovelty
    }: { banksData?: Array<Bank>; errors: FormError; paymentData: Payment | Partial<Payment> | null; paymentCustomerData: Customer; handleResolveSelectedNovelty: () => void },
    ref
  ) => {
    const { t } = useTranslation(['payments-to-review-management', 'common'])

    const [bankAccountNoveltiesData, setBankAccountNoveltiesData] = useState<
      Array<{
        message: string
        severity: string
      }>
    >([])
    const [banksDataToSelect, setBanksDataToSelect] = useState<Array<Bank>>([])
    const [acceptDuplicateAnyway, setAcceptDuplicateAnyway] = useState<boolean>(false)
    const [acceptBankAccountNumberAnyway, setAcceptBankAccountNumberAnyway] = useState<boolean>(false)
    const [dialogCustomerBankAccountsIsOpen, setDialogCustomerBankAccountsIsOpen] = useState<boolean>(false)
    const [selectedBankAccountData, setSelectedBankAccountData] = useState<Partial<BankAccount> | null>(null)

    useImperativeHandle(
      ref,
      () => {
        return {
          acceptDuplicateAnyway,
          selectedBankAccountData,
          acceptBankAccountNumberAnyway
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [selectedBankAccountData, acceptDuplicateAnyway, acceptBankAccountNumberAnyway]
    )

    useEffect(() => {
      return () => {
        setAcceptDuplicateAnyway(false)
        setAcceptBankAccountNumberAnyway(false)
      }
    }, [])

    useEffect(() => {
      if (paymentData && Array.isArray(paymentData.novelties)) {
        const bankAccountNoveltiesDataToDisplay = []

        for (let i = 0; i < paymentData.novelties.length; i++) {
          const paymentNovelty = paymentData.novelties[i]
          const bankAccountPaymentNoveltyToDisplayData = {
            message: '',
            severity: ''
          }

          if (paymentNovelty.type === 'empty-bank-code') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'danger'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.emptyBankCode')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'empty-bank-name') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'danger'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.emptyBankName')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'unknown-bank-code') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.unknownBankCode')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'unknown-bank-name') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.unknownBankName')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'wrong-bank-account-number') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'danger'
            bankAccountPaymentNoveltyToDisplayData.message =
              paymentNovelty.wrongBankAccountWarning === 'The bank account number has to have only digits.' ? t('noveltiesDescription.wrongBankAccountNumber') : String(paymentNovelty.wrongBankAccountWarning)

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'empty-bank-account-number') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'danger'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.emptyBankAccountNumber')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'empty-bank-account-type') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.emptyBankAccountTypeWarning ?? t('noveltiesDescription.emptyBankAccountType')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'wrong-bank-account-type') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountTypeWarning ?? t('noveltiesDescription.wrongBankAccountType')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'duplicate-bank-account-number') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.duplicateBankAccountNumber')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }

          if (paymentNovelty.type === 'bank-account-number-not-registered') {
            bankAccountPaymentNoveltyToDisplayData.severity = 'warning'
            bankAccountPaymentNoveltyToDisplayData.message = paymentNovelty.wrongBankAccountWarning ?? t('noveltiesDescription.bankAccountNumberNotRegistered')

            bankAccountNoveltiesDataToDisplay.push(bankAccountPaymentNoveltyToDisplayData)
          }
        }

        setBankAccountNoveltiesData(bankAccountNoveltiesDataToDisplay)
      }

      if (banksData && paymentData) {
        const banksDataToDisplay = banksData.filter((bankData) => bankData.achCode && bankData.name)
        setBanksDataToSelect(banksDataToDisplay)
        const bankAccountType = paymentData.bankAccountType === '27' ? '1' : paymentData.bankAccountType === '37' ? '2' : paymentData.bankAccountType // --> Esto lo hacemos por el formato del cliente

        setSelectedBankAccountData({
          bankAccountType: bankAccountType ?? '',
          bankAchCode: paymentData.bankAchCode ?? '',
          bankNumber: paymentData.bankAccountNumber ?? '',
          bankName: paymentData.bankName?.toUpperCase() ?? ''
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentData, banksData])

    const handleSelectRegisteredBankAccount = (selectedRegsiteredBankAccount: BankAccount) => {
      setSelectedBankAccountData({
        bankNumber: selectedRegsiteredBankAccount.bankNumber ?? '',
        bankAchCode: selectedRegsiteredBankAccount.bankAchCode ?? '',
        bankAccountType: selectedRegsiteredBankAccount.bankAccountType ?? '',
        bankName: selectedRegsiteredBankAccount.bankName?.toUpperCase() ?? ''
      })
      setDialogCustomerBankAccountsIsOpen(false)
    }

    return (
      <>
        <div
          style={{ borderBottom: 'none' }}
          className="document-findings-edit-bank-account-row-container"
        >
          {Array.isArray(bankAccountNoveltiesData) && bankAccountNoveltiesData.length > 0 && (
            <div className="novelty-modal-content-container">
              {bankAccountNoveltiesData.map(
                (
                  bankAccountNovelty: {
                    message: string
                    severity: string
                  },
                  index: number
                ) => (
                  <div
                    key={index}
                    className="info-data-container"
                    style={{ alignItems: 'center', justifyContent: 'center' }}
                  >
                    <div className="icon-container">
                      <img
                        src={bankAccountNovelty.severity === 'danger' ? DangerIcon : WarningIcon}
                        alt="novelty-icon"
                      />
                    </div>
                    <div
                      className="info-data-text"
                      style={bankAccountNovelty.severity === 'danger' ? { color: '#EF4444' } : { color: '#F59E0B' }}
                    >
                      {bankAccountNovelty.message}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
          <div className="document-findings-edit-bank-account-row-item-container">
            <TextField
              select
              fullWidth
              variant="standard"
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
              inputProps={{
                style: { textAlign: 'center' }
              }}
              label={t('resolveNoveltyModal.bankAccount.bankName')}
              error={!!errors.bankName}
              helperText={errors.bankName}
              value={
                selectedBankAccountData && selectedBankAccountData.bankName && selectedBankAccountData.bankAchCode
                  ? `${selectedBankAccountData.bankName?.toUpperCase()}-${selectedBankAccountData.bankAchCode?.toUpperCase()}`
                  : ''
              }
              onChange={(event) => {
                setSelectedBankAccountData((prevSelectedBankAccountState) => {
                  return {
                    ...prevSelectedBankAccountState,
                    bankName: event.target.value.split('-')[0],
                    bankAchCode: event.target.value.split('-')[1]
                  }
                })
              }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('resolveNoveltyModal.bankAccount.select')}
              </MenuItem>
              {banksDataToSelect?.map((bankData: Bank, index: number) => {
                return (
                  <MenuItem
                    key={index}
                    value={`${bankData.name.toUpperCase()}-${bankData.achCode.toUpperCase()}`}
                  >
                    {bankData.name.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
          <div className="document-findings-edit-bank-account-row-item-container">
            <div className="document-findings-edit-bank-account-row-item">
              <TextField
                select
                fullWidth
                variant="standard"
                sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px', textAlign: 'start' } }}
                label={t('resolveNoveltyModal.bankAccount.bankAccountType')}
                error={!!errors.bankAccountType}
                helperText={errors.bankAccountType}
                value={selectedBankAccountData ? selectedBankAccountData.bankAccountType : ''}
                onChange={(event) => {
                  setSelectedBankAccountData((prevSelectedBankAccountState) => {
                    return {
                      ...prevSelectedBankAccountState,
                      bankAccountType: event.target.value
                    }
                  })
                }}
              >
                <MenuItem
                  disabled
                  value=""
                >
                  {t('resolveNoveltyModal.bankAccount.select')}
                </MenuItem>
                {Object.keys(customersBankAccountType).map((bankAccountType: string, index: number) => {
                  return (
                    <MenuItem
                      key={index}
                      value={bankAccountType as string}
                    >
                      {(customersBankAccountType as any)[bankAccountType].toUpperCase()}
                    </MenuItem>
                  )
                })}
              </TextField>
            </div>
            <div className="document-findings-edit-bank-account-row-item">
              <TextField
                fullWidth
                variant="standard"
                sx={textFieldWhiteBg}
                error={!!errors.bankNumber}
                helperText={errors.bankNumber}
                value={selectedBankAccountData ? selectedBankAccountData.bankNumber : ''}
                label={t('resolveNoveltyModal.bankAccount.bankAccountNumber')}
                onChange={(event) => {
                  setSelectedBankAccountData((prevSelectedBankAccountState) => {
                    return {
                      ...prevSelectedBankAccountState,
                      bankNumber: event.target.value
                    }
                  })
                }}
              />
              {errors.bankAccountDuplicated && (
                <Button
                  fullWidth
                  variant="text"
                  sx={baseButtonTextWhiteBg}
                  className="actions-bar-button-action"
                  onClick={() => {
                    setAcceptDuplicateAnyway(true)

                    setTimeout(() => {
                      handleResolveSelectedNovelty()
                    }, 100)
                  }}
                >
                  <div className="actions-bar-button-action-text">{t('buttonsTexts.acceptDuplicateAnyway')}</div>
                </Button>
              )}
              {errors.bankAccountNumberLength && (
                <Button
                  fullWidth
                  variant="text"
                  sx={baseButtonTextWhiteBg}
                  className="actions-bar-button-action"
                  onClick={() => {
                    setAcceptBankAccountNumberAnyway(true)

                    setTimeout(() => {
                      handleResolveSelectedNovelty()
                    }, 100)
                  }}
                >
                  <div className="actions-bar-button-action-text">{t('buttonsTexts.acceptBankAccountLengthAnyway')}</div>
                </Button>
              )}
            </div>
          </div>
          {Array.isArray(paymentCustomerData?.bankAccountsData) && paymentCustomerData?.bankAccountsData.length > 0 && String(paymentData?.status) !== 'returned' && String(paymentData?.status) !== 'rejected' && (
            <Button
              fullWidth
              variant="text"
              sx={baseButtonTextWhiteBg}
              className="actions-bar-button-action"
              onClick={() => {
                setDialogCustomerBankAccountsIsOpen(true)
              }}
            >
              <div className="actions-bar-button-action-text">{t('buttonsTexts.assingRegisteredBankAccount')}</div>
            </Button>
          )}
        </div>
        {dialogCustomerBankAccountsIsOpen && (
          <Dialog
            hideActionsButtons
            actionButtonText=""
            handleDialogAction={() => console.log('entro')}
            dialogTitle={t('customerBanksAccountsModal.title')}
            handleDialogOpenState={(openState: boolean) => {
              setDialogCustomerBankAccountsIsOpen(openState)
            }}
          >
            <div className="registered-bank-account-content-container">
              {Array.isArray(paymentCustomerData.bankAccountsData) && (
                <>
                  {paymentCustomerData.bankAccountsData.map((bankAccountData: BankAccount, index: number) => {
                    return (
                      <RegisteredBankAccountItem
                        key={index}
                        bankAccountData={bankAccountData}
                        handleSelectRegisteredBankAccount={handleSelectRegisteredBankAccount}
                      />
                    )
                  })}
                </>
              )}
            </div>
          </Dialog>
        )}
      </>
    )
  }
)
